import type {
  MovementAssignmentSnapshot,
  MovementCompensation,
  MovementType,
} from '@/api/services/movements.service';
import { formatCurrency } from '@/lib/formatters';

export const MOVEMENT_TYPE_LABELS: Record<MovementType, string> = {
  transfer: 'Transfer',
  promotion: 'Promotion',
  demotion: 'Demotion',
  salary_revision: 'Salary revision',
};

/** Human label for a movement type; unknown server values degrade to a readable string. */
export function movementTypeLabel(type: string | null | undefined): string {
  if (!type) return 'Movement';
  if (Object.hasOwn(MOVEMENT_TYPE_LABELS, type)) return MOVEMENT_TYPE_LABELS[type as MovementType];
  const text = type.replaceAll('_', ' ');
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** Readable names for the assignment fields a movement can change. */
const FIELD_LABELS: Record<string, string> = {
  departmentId: 'Department',
  teamId: 'Team',
  positionId: 'Position',
  jobTitleId: 'Job title',
  gradeId: 'Grade',
  employmentTypeId: 'Employment type',
  reportingManagerUserId: 'Reporting manager',
  workLocation: 'Work location',
  compensation: 'Compensation',
};

export function movementFieldLabel(field: string): string {
  if (Object.hasOwn(FIELD_LABELS, field)) return FIELD_LABELS[field];
  // Unknown fields from a newer backend: "someFieldId" → "Some field".
  const words = field
    .replace(/Id$/, '')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replaceAll('_', ' ')
    .toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** Lists changed fields as labels; tolerates a missing or malformed array. */
export function movementChangeSummary(changedFields: unknown): string {
  if (!Array.isArray(changedFields)) return '—';
  const labels = changedFields
    .filter((field): field is string => typeof field === 'string')
    .map(movementFieldLabel);
  return labels.join(', ') || '—';
}

/**
 * Resolves one side of an assignment change to something a person can read:
 * a structure name when known, the literal for free-text fields, otherwise a
 * short reference so two distinct unknown ids still look different.
 */
export function formatAssignmentValue(
  field: string,
  snapshot: MovementAssignmentSnapshot | undefined | null,
  nameById: ReadonlyMap<string, string>
): string {
  const raw = snapshot ? (snapshot as Record<string, unknown>)[field] : undefined;
  if (raw === null || raw === undefined || raw === '') return 'None';
  if (typeof raw !== 'string') return String(raw);
  if (field === 'workLocation') return raw;
  return nameById.get(raw) ?? `Record …${raw.slice(-6)}`;
}

export function formatCompensation(value: MovementCompensation | undefined | null): string {
  if (!value || typeof value !== 'object') return 'Not recorded';
  const hourly = value.compensationType === 'hourly';
  const amount = hourly ? value.hourlyRate : value.baseSalary;
  const kind = hourly ? 'Hourly' : 'Salaried';
  if (typeof amount !== 'number' || !Number.isFinite(amount)) return kind;
  return `${kind} · ${formatCurrency(amount, value.currency)}${hourly ? ' / hour' : ''}`;
}
